import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import ScriptActions from "Redux/ScriptRedux";

export default function ScriptsPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [scripts, setScripts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  const loadScripts = useCallback(() => {
    setIsLoading(true);
    setError("");
    dispatch(ScriptActions.scriptsListRequest(null, (data, loadError) => {
      setIsLoading(false);
      if (loadError) {
        setError(loadError || "Failed to load scripts");
        console.error("Failed to load scripts:", loadError);
        return;
      }
      setScripts(data.scripts || data || []);
    }));
  }, [dispatch]);

  useEffect(() => {
    loadScripts();
  }, [loadScripts]);

  const handleScriptClick = (scriptId) => {
    navigate(`/scripts/${scriptId}`);
  };

  const getStatusBadge = (status) => {
    const config = {
      completed: { style: "bg-green-100 text-green-700", label: "Completed" },
      pending: { style: "bg-yellow-100 text-yellow-700", label: "Pending" },
      processing: { style: "bg-blue-100 text-blue-700", label: "Processing" },
      failed: { style: "bg-red-100 text-red-700", label: "Failed" },
    };
    return config[status] || { style: "bg-gray-100 text-gray-700", label: status || "Unknown" };
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Scripts</h1>
          <p className="text-gray-500 mt-1">
            {isLoading ? (
              <span className="inline-block w-20 h-4 bg-gray-200 rounded animate-pulse" />
            ) : (
              `${scripts.length} script${scripts.length !== 1 ? "s" : ""}`
            )}
          </p>
        </div>
        <button
          onClick={() => navigate("/")}
          className="px-4 py-2.5 min-h-[44px] bg-primary-500 text-white text-sm font-medium rounded-lg hover:bg-primary-600 transition-colors flex items-center"
        >
          + New Script
        </button>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-100 rounded-xl mb-6 flex items-center justify-between">
          <p className="text-red-600">{error}</p>
          <button
            onClick={loadScripts}
            className="text-sm font-medium text-red-600 hover:text-red-700 underline transition-colors"
          >
            Retry
          </button>
        </div>
      )}

      {/* Continue where you left off */}
      {scripts.length > 0 && (() => {
        const inProgress = scripts.find(s => s.status === "pending" || s.status === "processing");
        if (!inProgress) return null;
        return (
          <button
            onClick={() => handleScriptClick(inProgress.id)}
            className="w-full mb-6 p-4 bg-primary-50 border border-primary-200 rounded-xl hover:bg-primary-100 transition-all text-left flex items-center gap-4"
          >
            <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.348a1.125 1.125 0 010 1.971l-11.54 6.347a1.125 1.125 0 01-1.667-.985V5.653z" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-primary-700">Continue where you left off</p>
              <p className="text-sm text-primary-600 truncate">{inProgress.topic || "Untitled Script"}</p>
              {inProgress.current_step && (
                <p className="text-xs text-primary-500 mt-0.5 capitalize">Step: {inProgress.current_step}</p>
              )}
            </div>
            <svg className="w-5 h-5 text-primary-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
            </svg>
          </button>
        );
      })()}

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="p-4 bg-white rounded-xl border border-gray-200 animate-pulse">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="h-5 bg-gray-200 rounded w-3/4 mb-2" />
                  <div className="h-4 bg-gray-100 rounded w-full mb-1" />
                  <div className="h-4 bg-gray-100 rounded w-2/3 mb-3" />
                  <div className="flex items-center gap-3">
                    <div className="h-3 bg-gray-100 rounded w-24" />
                    <div className="h-3 bg-gray-100 rounded w-16" />
                  </div>
                </div>
                <div className="h-6 bg-gray-200 rounded-full w-20" />
              </div>
            </div>
          ))}
        </div>
      ) : scripts.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-2xl border border-gray-200">
          <div className="w-20 h-20 mx-auto mb-6 bg-primary-50 rounded-2xl flex items-center justify-center">
            <svg className="w-10 h-10 text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Create your first video</h3>
          <p className="text-gray-500 mb-8 max-w-sm mx-auto">
            Turn any idea into a professional AI-generated video in minutes. No filming required.
          </p>
          <button
            onClick={() => navigate("/")}
            className="px-8 py-3 bg-primary-500 text-white font-semibold rounded-xl hover:bg-primary-600 transition-all shadow-lg shadow-primary-500/25 hover:shadow-xl hover:shadow-primary-500/30"
          >
            Create Your First Video
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {scripts.map((script) => (
            <button
              key={script.id}
              onClick={() => handleScriptClick(script.id)}
              className="w-full p-4 bg-white rounded-xl border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all text-left"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-gray-900 truncate">
                    {script.topic || "Untitled Script"}
                  </h3>
                  {script.content && (
                    <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                      {script.content.length > 150 ? script.content.substring(0, 150) + "..." : script.content}
                    </p>
                  )}
                  <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                    <span>{formatDate(script.created_at)}</span>
                    {script.video_type && (
                      <span className="capitalize">{script.video_type}</span>
                    )}
                  </div>
                </div>
                <span className={`px-2.5 py-1 text-xs font-medium rounded-full flex-shrink-0 ${getStatusBadge(script.status).style}`}>
                  {getStatusBadge(script.status).label}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
